"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { type MouseEvent, useState } from "react";

import { Link } from "@/navigation";

interface FinishRow {
	keyPrefix: string;
	image: string;
}

interface IndustrySurfaceFinishesProps {
	namespace: string;
	finishes: readonly FinishRow[];
	brandColor?: string;
}

const HOVER_PREVIEW_WIDTH = 224;
const HOVER_PREVIEW_HEIGHT = 224;
const HOVER_PREVIEW_OFFSET = 18;
const VIEWPORT_GUTTER = 12;

export default function IndustrySurfaceFinishes({
	namespace,
	finishes,
	brandColor = "#64a525",
}: IndustrySurfaceFinishesProps) {
	const t = useTranslations(namespace);
	const [hoveredFinish, setHoveredFinish] = useState<FinishRow | null>(null);
	const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });

	const updatePreviewPosition = (event: MouseEvent<HTMLTableRowElement>) => {
		const maxX = window.innerWidth - HOVER_PREVIEW_WIDTH - VIEWPORT_GUTTER;
		const x = Math.max(VIEWPORT_GUTTER, Math.min(event.clientX + HOVER_PREVIEW_OFFSET, maxX));
		const preferredY = event.clientY + HOVER_PREVIEW_OFFSET;
		const y = Math.max(
			VIEWPORT_GUTTER,
			preferredY + HOVER_PREVIEW_HEIGHT > window.innerHeight - VIEWPORT_GUTTER
				? event.clientY - HOVER_PREVIEW_HEIGHT - HOVER_PREVIEW_OFFSET
				: preferredY,
		);

		setPreviewPosition({ x, y });
	};

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			{hoveredFinish && (
				<div
					className="pointer-events-none fixed z-50 hidden h-56 w-56 overflow-hidden border border-gray-200 bg-white shadow-xl md:block"
					style={{ left: previewPosition.x, top: previewPosition.y }}
					aria-hidden
				>
					<Image
						src={hoveredFinish.image}
						alt=""
						fill
						className="object-contain"
						sizes={`${HOVER_PREVIEW_WIDTH}px`}
					/>
				</div>
			)}
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-[1.3] text-black sm:mb-4 sm:text-[30px] sm:leading-[1.25] md:text-4xl md:leading-tight">
						<span className="block">{t("surfaceFinishesTitleFirstLine")}</span>
						<span className="block">{t("surfaceFinishesTitleSecondLine")}</span>
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("surfaceFinishesDesc")}</p>
				</header>

				<div className="hidden overflow-hidden border border-gray-200 md:block">
					<table className="w-full table-fixed border-collapse bg-white text-sm">
						<thead>
							<tr className="text-white" style={{ backgroundColor: brandColor }}>
								<th className="w-[22%] px-4 py-8 text-left font-semibold">{t("surfaceFinishesColName")}</th>
								<th className="w-[18%] px-4 py-8 text-center font-semibold">
									{t("surfaceFinishesColApplicable")}
								</th>
								<th className="w-[30%] px-4 py-8 text-center font-semibold">
									{t("surfaceFinishesColFeature")}
								</th>
								<th className="w-[15%] px-4 py-8 text-center font-semibold">
									{t("surfaceFinishesColColor")}
								</th>
								<th className="w-[15%] px-4 py-8 text-center font-semibold">
									{t("surfaceFinishesColThickness")}
								</th>
							</tr>
						</thead>
						<tbody>
							{finishes.map((finish, index) => (
								<tr
									key={finish.keyPrefix}
									className={`group border-t border-[#efefef] transition-colors hover:bg-[#f5f7fa] ${
										index % 2 === 1 ? "bg-white" : "bg-[#f7f7f7]"
									}`}
									onMouseEnter={(event) => {
										setHoveredFinish(finish);
										updatePreviewPosition(event);
									}}
									onMouseMove={updatePreviewPosition}
									onMouseLeave={() => setHoveredFinish(null)}
								>
									<td className="px-4 py-4">
										<div className="flex items-center gap-3">
											<div className="relative h-16 w-16 shrink-0 overflow-hidden bg-gray-100">
												<Image
													src={finish.image}
													alt={t(`${finish.keyPrefix}Alt`)}
													fill
													className="object-cover transition-transform duration-300 group-hover:scale-110"
													sizes="64px"
												/>
											</div>
											<span className="font-medium text-black">{t(`${finish.keyPrefix}Name`)}</span>
										</div>
									</td>
									<td className="px-4 py-4 text-center text-gray-700">
										{t(`${finish.keyPrefix}Applicable`)}
									</td>
									<td className="px-4 py-4 text-center leading-relaxed text-gray-700">
										{t(`${finish.keyPrefix}Feature`)}
									</td>
									<td className="px-4 py-4 text-center text-gray-700">{t(`${finish.keyPrefix}Color`)}</td>
									<td className="px-4 py-4 text-center text-gray-700">{t(`${finish.keyPrefix}Thickness`)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="grid grid-cols-1 gap-4 md:hidden">
					{finishes.map((finish) => (
						<div
							key={finish.keyPrefix}
							className="overflow-hidden rounded-lg border border-gray-200 bg-white"
						>
							<div className="flex items-center gap-3 border-b border-gray-100 bg-[#ebf5ea] px-4 py-3">
								<div className="relative h-12 w-12 shrink-0 overflow-hidden rounded bg-gray-100">
									<Image
										src={finish.image}
										alt={t(`${finish.keyPrefix}Alt`)}
										fill
										className="object-cover"
										sizes="48px"
									/>
								</div>
								<span className="font-semibold text-black">{t(`${finish.keyPrefix}Name`)}</span>
							</div>
							<div className="divide-y divide-gray-100 px-4 py-2">
								{["Applicable", "Feature", "Color", "Thickness"].map((field) => (
									<div key={field} className="flex justify-between gap-4 py-2 text-sm">
										<span className="shrink-0 font-medium text-gray-500">
											{t(`surfaceFinishesCol${field}`)}
										</span>
										<span className="text-right text-black">{t(`${finish.keyPrefix}${field}`)}</span>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				<div className="mt-8 flex justify-center sm:mt-10 md:mt-12">
					<Link
						href="/surface-finishing"
						className="inline-flex items-center justify-center rounded px-8 py-3 text-base font-semibold text-white transition-opacity hover:text-white hover:opacity-90"
						style={{ backgroundColor: brandColor }}
					>
						{t("surfaceFinishesCtaBtn")}
					</Link>
				</div>
			</div>
		</section>
	);
}
